import { ExtRegionParams, ApiFragment, WorkerStatus } from '@/types/workbench';
import Edit from '@/types/edit';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

export interface Worker {
  id: string;
  name: string;
  endpointUrl: string;
  streamUrl: string;
  lastSeen: string;
  lastSeenDelta: number;
  graduationDate: string;
  converting: boolean;
}

export type CurrentPositionSource =
  | 'transcript'
  | 'video-playing'
  | 'video-scrubbing'
  | 'video-initial-load'
  | 'waveform-click'
  | 'waveform-minimap'
  | 'waveform-segment-resize'
  | 'segment-click';

/**
 * Represents the current playback position with metadata about who set it
 * Used to track position changes across different UI components
 */
export interface CurrentPosition {
  /** Time in seconds */
  timeSec: number;
  /** Source component that last updated the position */
  source: CurrentPositionSource;
}

// ---------------------------------------------------------------------------
// PlaybackPositionContext — isolated so position updates (4×/sec during
// playback) don't trigger re-renders in components that only need fragments,
// regionParams, edits, etc.
// ---------------------------------------------------------------------------

export interface PlaybackPositionContextValue {
  currentPosition?: CurrentPosition;
  setCurrentPosition(timeSec: number, source: CurrentPositionSource): void;
}

export const PlaybackPositionContext = React.createContext<PlaybackPositionContextValue>({
  setCurrentPosition: () => {
    return;
  },
});

/** Convenience hook for accessing playback position */
export function usePlaybackPosition(): PlaybackPositionContextValue {
  return useContext(PlaybackPositionContext);
}

// ---------------------------------------------------------------------------
// WorkbenchContext — stable state that does NOT change on every timeupdate
// ---------------------------------------------------------------------------

export interface WorkbenchContextValue {
  workbenchState: any;
  setWorkbenchState: React.Dispatch<React.SetStateAction<any>>;
  selectStartRegion: string | null;
  setSelectStartRegion: React.Dispatch<React.SetStateAction<string | null>>;
  selectEndRegion: string | null;
  setSelectEndRegion: React.Dispatch<React.SetStateAction<string | null>>;
  worker?: Worker;
  fragments: ApiFragment[];
  setFragments: React.Dispatch<React.SetStateAction<ApiFragment[]>>;
  edits: Edit[];
  setEdits: React.Dispatch<React.SetStateAction<Edit[]>>;
  regionParams: ExtRegionParams[];
  setRegionParams: React.Dispatch<React.SetStateAction<ExtRegionParams[]>>;
  selectedRegions: ExtRegionParams[];
  setSelectedRegions(regions: ExtRegionParams[]): void;
  lastRegionSelectOriginator: 'region' | 'student';
  workerStatus?: WorkerStatus | null;
  setWorkerStatus: React.Dispatch<React.SetStateAction<WorkerStatus | null>>;
  workerId: string;
  setWorkerId: React.Dispatch<React.SetStateAction<string>>;
  regionsVisible: boolean;
  setRegionsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  controlState: any;
  setControlState: React.Dispatch<React.SetStateAction<any>>;
  isVideoPaused: boolean;
  setIsVideoPaused: React.Dispatch<React.SetStateAction<boolean>>;
  isVideoMuted: boolean;
  setIsVideoMuted: React.Dispatch<React.SetStateAction<boolean>>;
}

export const WorkbenchContext = React.createContext<WorkbenchContextValue>({
  workbenchState: {},
  lastRegionSelectOriginator: 'region',
  selectedRegions: [],
  setSelectedRegions: () => {
    return;
  },
  setWorkbenchState: () => {
    return;
  },
  selectStartRegion: null,
  setSelectStartRegion: () => {
    return;
  },
  selectEndRegion: null,
  setSelectEndRegion: () => {
    return;
  },
  fragments: [],
  setFragments: () => {
    return;
  },
  edits: [],
  setEdits: () => {
    return;
  },
  regionParams: [],
  setRegionParams: () => {
    return;
  },
  setWorkerStatus: () => {
    return;
  },
  workerId: '',
  setWorkerId: () => {
    return;
  },
  regionsVisible: true,
  setRegionsVisible: () => {
    return;
  },
  controlState: {
    toggleRegions: 'disabled',
    deleteRegions: 'disabled',
    createRegions: 'normal',
    assignRegions: 'disabled',
    lockRegions: 'disabled',
    produceRegions: 'disabled',
  },
  setControlState: () => {
    return;
  },
  isVideoPaused: false,
  setIsVideoPaused: () => {
    return;
  },
  isVideoMuted: true,
  setIsVideoMuted: () => {
    return;
  },
});

export interface VideoProviderProps {
  children: React.ReactNode;
}

export const WorkbenchProvider: React.FC<VideoProviderProps> = ({
  children,
}: VideoProviderProps) => {
  const [workbenchState, setWorkbenchState] = useState({});
  const [selectStartRegion, setSelectStartRegion] = useState<string | null>(null);
  const [selectEndRegion, setSelectEndRegion] = useState<string | null>(null);
  const [workerStatus, setWorkerStatus] = useState<WorkerStatus | null>(null);
  const [workerId, setWorkerId] = useState<string>('');
  const [fragments, setFragments] = useState<ApiFragment[]>([]);
  const [edits, setEdits] = useState<Edit[]>([]);
  const [regionParams, setRegionParams] = useState<ExtRegionParams[]>([]);
  const [currentPosition, setCurrentPositionState] = useState<CurrentPosition>({
    timeSec: 0,
    source: 'video-initial-load',
  });
  const [selectedRegions, setRegion] = useState<ExtRegionParams[]>([]);
  const [regionsVisible, setRegionsVisible] = useState<boolean>(true);
  const [controlState, setControlState] = useState<any>({
    toggleRegions: 'disabled',
    deleteRegions: 'disabled',
    createRegions: 'normal',
    assignRegions: 'disabled',
    lockRegions: 'disabled',
    produceRegions: 'disabled',
  });
  const [lastRegionSelectOriginator, setLastRegionSelectOriginator] = useState<
    'region' | 'student'
  >('region');
  const [isVideoPaused, setIsVideoPaused] = useState<boolean>(false);
  const [isVideoMuted, setIsVideoMuted] = useState<boolean>(true);

  /**
   * Updates the current playback position with source tracking
   * @param timeSec - Time in seconds
   * @param source - Component that is setting the position
   */
  const setCurrentPosition = useCallback((timeSec: number, source: CurrentPositionSource): void => {
    setCurrentPositionState((prev) => {
      if (prev.timeSec === timeSec && prev.source === source) return prev;
      return { timeSec, source };
    });
  }, []);

  const setSelectedRegions = useCallback(
    (regions: ExtRegionParams[]): void => {
      setRegionParams((prev) => prev.map((r) => ({ ...r, selected: false })));
      const _mapped = regions.map((r) => {
        if (!r) return r;
        return { ...r, selected: true };
      });
      setRegion(_mapped);
      setLastRegionSelectOriginator('region');
    },
    [setRegionParams],
  );

  useEffect(() => {
    const hasActiveSelection = selectedRegions.length > 0;
    const regionsLocked = hasActiveSelection && selectedRegions.every((r) => r.locked);
    const regionsAssigned = hasActiveSelection;
    const lockStatus = !hasActiveSelection ? 'disabled' : regionsLocked ? 'normal' : 'active';
    const produceStatus = regionsAssigned ? 'active' : 'disabled';
    setControlState({
      toggleRegions: regionsVisible ? 'normal' : 'active',
      deleteRegions: hasActiveSelection ? 'active' : 'disabled',
      createRegions: 'normal',
      assignRegions: 'disabled',
      lockRegions: lockStatus,
      produceRegions: produceStatus,
    });
  }, [selectedRegions, regionsVisible]);

  // Playback position value — in its own context so position updates don't
  // invalidate the main WorkbenchContext consumers
  const playbackPositionValue = useMemo(
    () => ({ currentPosition, setCurrentPosition }),
    [currentPosition, setCurrentPosition],
  );

  const contextValue = useMemo(
    () => ({
      workbenchState,
      selectedRegions,
      lastRegionSelectOriginator,
      setSelectedRegions,
      setWorkbenchState,
      selectStartRegion,
      setSelectStartRegion,
      selectEndRegion,
      setSelectEndRegion,
      fragments,
      setFragments,
      edits,
      setEdits,
      regionParams,
      setRegionParams,
      workerStatus,
      setWorkerStatus,
      workerId,
      setWorkerId,
      regionsVisible,
      setRegionsVisible,
      controlState,
      setControlState,
      isVideoPaused,
      setIsVideoPaused,
      isVideoMuted,
      setIsVideoMuted,
    }),
    [
      workbenchState,
      selectedRegions,
      lastRegionSelectOriginator,
      setSelectedRegions,
      selectStartRegion,
      selectEndRegion,
      fragments,
      edits,
      regionParams,
      workerStatus,
      workerId,
      regionsVisible,
      controlState,
      isVideoPaused,
      isVideoMuted,
    ],
  );

  return (
    <PlaybackPositionContext.Provider value={playbackPositionValue}>
      <WorkbenchContext.Provider value={contextValue}>{children}</WorkbenchContext.Provider>
    </PlaybackPositionContext.Provider>
  );
};
